import { useState, useRef, useEffect, type ReactNode } from 'react'
import { Box, Flex, Text, Portal } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { LuEllipsisVertical as MoreVertical, LuSearchX, LuTriangleAlert } from 'react-icons/lu'
import { ACCENT, ACCENT_SOFT, EASE_OUT, FONT_DISPLAY, FONT_MONO, SHADOW_LG, SHADOW_MD } from './tokens'

const MotionBox = motion.create(Box)

// --- Types ---

export interface TableAction {
  label: string
  onClick: () => void
  color?: string
  icon?: ReactNode
}

export interface DataTableColors {
  bgCard: string
  bgHover: string
  bgPanel: string
  border: string
  text: string
  textMuted: string
  textDimmed?: string
}

export interface DataTableProps<T> {
  headers: string[]
  data: T[]
  loading?: boolean
  error?: string | null
  emptyMessage?: string
  loadingMessage?: string
  totalCount?: number
  renderRow: (item: T) => ReactNode
  actions?: (item: T) => TableAction[]
  keyExtractor: (item: T) => string
  colors: DataTableColors
  footer?: ReactNode
  /** @internal Used by DataList to render table without card wrapper */
  _noCard?: boolean
}

// --- Cell sub-component ---

function Cell({ children }: { children: ReactNode }) {
  return (
    <Box as="td" px={4} py={3.5} verticalAlign="middle">
      {children}
    </Box>
  )
}

// --- Estados (carga / vacío / error) ---

/** Esqueleto con el mismo número de columnas: la tabla "toma forma" antes de llegar los datos. */
export function TableSkeleton({ columns, rows = 6, colors }: { columns: number; rows?: number; colors: DataTableColors }) {
  return (
    <Box px={4} py={2} aria-busy="true">
      {Array.from({ length: rows }).map((_, r) => (
        <Flex key={r} gap={6} py={3.5} borderBottom={r < rows - 1 ? '1px solid' : 'none'} borderColor={colors.border} align="center">
          {Array.from({ length: Math.min(columns, 7) }).map((__, c) => (
            <Box
              key={c}
              flex={c === 1 ? 2 : 1}
              h="10px"
              borderRadius="6px"
              bg={colors.bgHover}
              position="relative"
              overflow="hidden"
              opacity={1 - r * 0.1}
            >
              <motion.span
                animate={{ x: ['-100%', '220%'] }}
                transition={{ duration: 1.3, repeat: Infinity, ease: 'easeInOut', delay: c * 0.05 + r * 0.04 }}
                style={{ position: 'absolute', inset: 0, width: '50%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.75), transparent)' }}
              />
            </Box>
          ))}
        </Flex>
      ))}
    </Box>
  )
}

export function TableMessage({ kind, message, colors }: { kind: 'empty' | 'error'; message: string; colors: DataTableColors }) {
  const Icon = kind === 'error' ? LuTriangleAlert : LuSearchX
  return (
    <MotionBox initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} py={12} px={6} textAlign="center">
      <Flex mx="auto" w="52px" h="52px" borderRadius="16px" align="center" justify="center"
        bg={kind === 'error' ? '#fde3de' : ACCENT_SOFT} color={kind === 'error' ? '#d6402b' : ACCENT}>
        <Icon size={24} />
      </Flex>
      <Text mt={4} fontFamily={FONT_DISPLAY} fontWeight={700} fontSize="16px" color={colors.text}>
        {kind === 'error' ? 'No pudimos cargar esto' : 'Sin resultados'}
      </Text>
      <Text mt={1} fontSize="13.5px" color={colors.textMuted} maxW="420px" mx="auto">{message}</Text>
    </MotionBox>
  )
}

// --- ActionsCell (internal) ---

function ActionsCell({
  itemId,
  actions,
  colors,
  openMenuId,
  setOpenMenuId,
  menuRef,
}: {
  itemId: string
  actions: TableAction[]
  colors: DataTableColors
  openMenuId: string | null
  setOpenMenuId: (id: string | null) => void
  menuRef: React.RefObject<HTMLDivElement | null>
}) {
  const isOpen = openMenuId === itemId
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null)

  // El menú se porta a <body> con position: fixed para escapar del scroll horizontal
  // de la tabla; se cierra al hacer scroll/resize para que nunca se despegue del botón.
  const handleTriggerClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isOpen) {
      setOpenMenuId(null)
      return
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    setMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right })
    setOpenMenuId(itemId)
  }

  useEffect(() => {
    if (!isOpen) return
    // El propio clic puede desplazar la tabla (foco en una celda fuera de vista); ese
    // scroll no debe cerrar el menú que acaba de abrirse.
    const openedAt = Date.now()
    const close = () => { if (Date.now() - openedAt > 250) setOpenMenuId(null) }
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [isOpen, setOpenMenuId])

  return (
    <Box as="td" px={3} py={3.5} verticalAlign="middle" textAlign="right">
      <Box
        as="button" {...{ type: 'button', 'aria-label': 'Acciones' }}
        color={isOpen ? ACCENT : colors.textMuted}
        bg={isOpen ? ACCENT_SOFT : 'transparent'}
        cursor="pointer"
        p={1.5}
        borderRadius="10px"
        _hover={{ bg: ACCENT_SOFT, color: ACCENT }}
        transition="all 0.15s"
        onClick={handleTriggerClick}
      >
        <MoreVertical size={18} />
      </Box>

      <Portal>
        <AnimatePresence>
          {isOpen && menuPos && (
            <motion.div
              ref={menuRef}
              initial={{ opacity: 0, scale: 0.94, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: -4 }}
              transition={{ type: 'spring', stiffness: 600, damping: 36 }}
              style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 1500, transformOrigin: 'top right' }}
            >
              <Box bg={colors.bgPanel} border="1px solid" borderColor={colors.border} borderRadius="14px" boxShadow={SHADOW_LG} p={1.5} minW="200px">
                {actions.map((action, i) => (
                  <motion.div key={action.label} initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.02 * i }}>
                    <Box
                      as="button" {...{ type: 'button' }}
                      display="flex"
                      alignItems="center"
                      gap={2.5}
                      width="100%"
                      textAlign="left"
                      px={3}
                      py={2.5}
                      borderRadius="9px"
                      fontSize="sm"
                      fontWeight={500}
                      color={action.color ?? colors.text}
                      cursor="pointer"
                      _hover={{ bg: action.color ? `${action.color}14` : colors.bgHover }}
                      transition="background 0.15s"
                      onClick={action.onClick}
                    >
                      {action.icon}
                      {action.label}
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Portal>
    </Box>
  )
}

// --- Main DataTable ---

function DataTableInner<T>({
  headers,
  data,
  loading = false,
  error = null,
  emptyMessage = 'No se encontraron resultados',
  totalCount,
  renderRow,
  actions,
  keyExtractor,
  colors,
  footer,
  _noCard = false,
}: DataTableProps<T>) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null)
      }
    }
    if (openMenuId !== null) {
      document.addEventListener('mousedown', handleClick)
      return () => document.removeEventListener('mousedown', handleClick)
    }
  }, [openMenuId])

  const displayHeaders = actions ? [...headers, ''] : headers

  const tableContent = (
    <>
      {loading ? (
        <TableSkeleton columns={displayHeaders.length} colors={colors} />
      ) : error ? (
        <TableMessage kind="error" message={error} colors={colors} />
      ) : data.length === 0 ? (
        <TableMessage kind="empty" message={emptyMessage} colors={colors} />
      ) : (
        <Box overflowX="auto">
          <Box as="table" width="100%" textAlign="left" css={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <Box as="thead">
              <Box as="tr">
                {displayHeaders.map((header, i) => (
                  <Box
                    as="th"
                    key={header || `action-col-${i}`}
                    px={4}
                    py={3}
                    fontFamily={FONT_MONO}
                    fontSize="10.5px"
                    textTransform="uppercase"
                    letterSpacing="0.1em"
                    fontWeight="500"
                    color={colors.textMuted}
                    whiteSpace="nowrap"
                    bg={colors.bgCard}
                    borderBottom="1px solid"
                    borderColor={colors.border}
                  >
                    {header}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box as="tbody">
              {data.map((item, idx) => {
                const id = keyExtractor(item)
                const itemActions = actions?.(item)
                const menuOpen = openMenuId === id

                return (
                  <motion.tr
                    key={id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.28, delay: Math.min(idx, 12) * 0.025, ease: EASE_OUT }}
                    className="k-row"
                    data-open={menuOpen || undefined}
                  >
                    {renderRow(item)}
                    {itemActions && (
                      <ActionsCell
                        itemId={id}
                        actions={itemActions}
                        colors={colors}
                        openMenuId={openMenuId}
                        setOpenMenuId={setOpenMenuId}
                        menuRef={menuRef}
                      />
                    )}
                  </motion.tr>
                )
              })}
            </Box>
          </Box>
        </Box>
      )}

      {/* Legacy footer */}
      {totalCount !== undefined && !footer && (
        <Flex px={4} py={3} borderTop="1px solid" borderColor={colors.border} justifyContent="space-between" alignItems="center">
          <Text fontSize="xs" color={colors.textMuted}>
            Mostrando {data.length} de {totalCount}
          </Text>
        </Flex>
      )}

      {footer}
    </>
  )

  if (_noCard) return tableContent

  return (
    <MotionBox
      bg={colors.bgPanel}
      border="1px solid"
      borderColor={colors.border}
      borderRadius="22px"
      overflow="hidden"
      boxShadow={SHADOW_MD}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.05, ease: EASE_OUT }}
    >
      {tableContent}
    </MotionBox>
  )
}

export const DataTable = Object.assign(DataTableInner, { Cell })
